import React, { useContext } from "react";
import { motion } from "framer-motion";
import StyleContext from "../../contexts/StyleContext";
import { contactInfo, socialMediaLinks } from "../../portfolio";
import SocialMedia from "../../components/socialMedia/SocialMedia";

export default function ContactModern() {
  const { isDark } = useContext(StyleContext);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const contactMethods = [
    {
      icon: "📧",
      title: "Email",
      value: contactInfo.email_address,
      href: `mailto:${contactInfo.email_address}`,
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: "📱",
      title: "Phone",
      value: contactInfo.number,
      href: `tel:${contactInfo.number}`,
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: "💬",
      title: "WhatsApp",
      value: "Chat Now",
      href: socialMediaLinks.whatsapp,
      color: "from-emerald-500 to-teal-500"
    }
  ].filter(method => method.value);

  return (
    <div className="relative py-20 md:py-32 overflow-hidden" id="contact">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-indigo-900/20 dark:to-purple-900/20" />

      {/* Floating Orbs */}
      <motion.div
        className="absolute -left-20 top-1/4 w-96 h-96 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute -right-20 bottom-1/4 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          x: [0, -50, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="relative z-10 container mx-auto px-4"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {/* Section Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <motion.div
            className="inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <span className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full text-sm font-semibold shadow-lg">
              {contactInfo.title}
            </span>
          </motion.div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            Get In Touch
          </h2>
          <p className={`text-lg md:text-xl max-w-3xl mx-auto ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}>
            {contactInfo.subtitle}
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Side - Contact Methods */}
            <motion.div variants={itemVariants} className="space-y-6">
              {/* Contact Cards */}
              {contactMethods.map((method, index) => (
                <motion.a
                  key={index}
                  href={method.href}
                  target={method.href.startsWith('http') ? '_blank' : undefined}
                  rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  whileHover={{
                    scale: 1.02,
                    x: 10,
                    transition: { duration: 0.2 }
                  }}
                  className="glass backdrop-blur-xl rounded-2xl p-6 bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/20 shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center gap-4 group"
                >
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${method.color} flex items-center justify-center text-3xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {method.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-sm font-semibold mb-1 ${
                      isDark ? "text-gray-400" : "text-gray-600"
                    }`}>
                      {method.title}
                    </h3>
                    <p className={`text-lg font-bold ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}>
                      {method.value}
                    </p>
                  </div>
                  <svg className={`w-5 h-5 group-hover:translate-x-2 transition-transform duration-300 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`} fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M9 5l7 7-7 7" />
                  </svg>
                </motion.a>
              ))}

              {/* Social Media */}
              <motion.div
                variants={itemVariants}
                className="glass backdrop-blur-xl rounded-2xl p-6 bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/20 shadow-xl"
              >
                <h3 className={`text-lg font-semibold mb-4 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}>
                  Connect on Social Media
                </h3>
                <SocialMedia />
              </motion.div>
            </motion.div>

            {/* Right Side - Illustration/Message */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col justify-center"
            >
              <div className="glass backdrop-blur-xl rounded-3xl p-8 md:p-12 bg-white/40 dark:bg-gray-800/40 border border-white/20 dark:border-gray-700/20 shadow-2xl">
                {/* Illustration */}
                <div className="mb-8">
                  <img
                    src={require(`../../assets/images/${
                      isDark ? "contactMailDark.svg" : "contactMail.webp"
                    }`)}
                    alt="Contact"
                    className="w-full h-auto max-w-md mx-auto"
                  />
                </div>

                {/* Message */}
                <div className="text-center">
                  <h3 className={`text-2xl font-bold mb-4 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}>
                    Let's Collaborate!
                  </h3>
                  <p className={`mb-6 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
                  </p>

                  {/* CTA Button */}
                  <motion.a
                    href={`mailto:${contactInfo.email_address}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/50 transition-all duration-300"
                  >
                    <span>Send a Message</span>
                    <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Decorative Element */}
        <motion.div
          variants={itemVariants}
          className="mt-20 flex justify-center"
        >
          <div className="flex items-center gap-2">
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-indigo-500" />
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-purple-500 to-transparent" />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
